import { useFormik } from "formik";
import * as Yup from "yup";
import { URL_REGEX } from "../../../utils/prepareURL";
import { VerticalSpacing } from "../../VerticalSpacing";
import { V3CaptionGrey35 } from "../../ReusableStyledComponents/V3CaptionGrey35";
import { V3TextArea } from "../../TextInput/V3TextInput";
import { RightAlignPanel } from "../../ReusableStyledComponents/RightAlignPanel";
import { V3Button } from "../../Button";
import { makeIdFromLength } from "../../../utils/makeIdFromLength";
import { V3ButtonColor } from "../../Button/V3Button.types";
import { getAddContentDescription, getAddContentInputLabel, getAddContentTextAreaPlaceholder } from "./AddContent.utils";
import { ManualImportPathTaken } from "../UniversalAddContentTracking";
import { ImportItem, V3ImportManualFormik } from "./AddContent.types";
import { InlineLink } from "../../ReusableStyledComponents/InlineLink";
import { PLUGIN_URL } from "../../../utils/pluginUrl";

import type { JSX } from "react";

interface AddContentImportProps {
    usedPlugin: boolean;
    importPathTaken: ManualImportPathTaken;
    url?: string;
    onURLSubmit: (items: ImportItem[]) => void;
    onBackHandler: () => void;
    onJumpToPDF: () => void;
}

export const AddContentImport = ({ onURLSubmit, onBackHandler, onJumpToPDF, importPathTaken, url = "", usedPlugin }: AddContentImportProps) => {
    const formik = useFormik<V3ImportManualFormik>({
        initialValues: {
            url
        },
        onSubmit: async (data) => {
            const lines = data.url?.split(/\r?\n/);
            const items: ImportItem[] = [];

            if (!lines) return;

            for (let index = 0; index < lines.length; index++) {
                const line = lines[index].trim();
                if (line.match(URL_REGEX)) {
                    items.push({
                        url: line,
                        contentRes: undefined,
                        id: makeIdFromLength(10)
                    });
                }
            }

            onURLSubmit(items);
        },
        validationSchema: Yup.object().shape({
            url: Yup.string()
                .required("Please enter a url")
                .trim()
                .test("is-valid-input", "Please add at least one valid url", (value: string | null | undefined) => {
                    if (!value) return false;
                    const lines = value?.split(/\r?\n/);
                    let valid = false;

                    for (let index = 0; index < lines.length; index++) {
                        const line = lines[index].trim();
                        if (line.match(URL_REGEX)) valid = true;
                    }

                    return valid;
                })
        })
    });

    let urlError: string | undefined | JSX.Element =
        formik.errors.hasOwnProperty("url") && formik.touched.hasOwnProperty("url") ? formik.errors.url : undefined;

    return (
        <>
            <VerticalSpacing bottom={!usedPlugin ? 18 : 32}>
                <V3CaptionGrey35>{getAddContentDescription(importPathTaken, url, onJumpToPDF)}</V3CaptionGrey35>
            </VerticalSpacing>
            {!usedPlugin && (
                <VerticalSpacing bottom={32}>
                    <V3CaptionGrey35>
                        Tip: Add work samples with a single click with the{" "}
                        <InlineLink href={PLUGIN_URL} target="_blank">
                            Authory Browser Extension
                        </InlineLink>{" "}
                        for Chrome.
                    </V3CaptionGrey35>
                </VerticalSpacing>
            )}
            <form onSubmit={formik.handleSubmit}>
                <VerticalSpacing bottom={24}>
                    <V3TextArea
                        style={{ minHeight: 250 }}
                        error={urlError}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.url || ""}
                        inputMode="url"
                        name="url"
                        label={getAddContentInputLabel(importPathTaken)}
                        placeholder={getAddContentTextAreaPlaceholder(importPathTaken)}
                    />
                </VerticalSpacing>
                <RightAlignPanel>
                    <div>
                        <V3Button text="Back" autoWidth onClick={onBackHandler} color={V3ButtonColor.secondary} />
                    </div>
                    <div>
                        <V3Button text="Add" type="submit" autoWidth />
                    </div>
                </RightAlignPanel>
            </form>
        </>
    );
};
